import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getCategoryColors } from "@/lib/categoryColors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Clock, Wrench, Lock, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useAnnonces, getUrgencyLabel } from "@/hooks/useAnnonces";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";

const AnnoncePreviewSection = () => {
  const { t, locale } = useLanguage();
  const lp = useLocalizedPath();
  const { data: annonces = [], isLoading } = useAnnonces();

  if (isLoading) {
    return (
      <section className="container py-16">
        <div className="text-center text-muted-foreground py-8">{t("preview.loading")}</div>
      </section>
    );
  }

  if (annonces.length === 0) return null;

  const formatBudget = (min?: number | null, max?: number | null) => {
    const currency = locale === "ar" ? "دج" : "DA";
    if (min && max) return `${min.toLocaleString()} – ${max.toLocaleString()} ${currency}`;
    if (min) return locale === "ar" ? `ابتداءً من ${min.toLocaleString()} ${currency}` : `À partir de ${min.toLocaleString()} ${currency}`;
    if (max) return locale === "ar" ? `حتى ${max!.toLocaleString()} ${currency}` : `Jusqu'à ${max!.toLocaleString()} ${currency}`;
    return null;
  };

  return (
    <section className="container py-16">
      <div className="mb-8 text-center">
        <h2 className="font-display text-3xl font-bold md:text-4xl">{t("preview.title")}</h2>
        <p className="mt-3 text-muted-foreground">{t("preview.subtitle")}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {annonces.slice(0, 6).map((annonce) => {
          const catKey = annonce.category || annonce.metier;
          const catColors = getCategoryColors(catKey);
          return (
          <Card key={annonce.id} className={cn("group overflow-hidden transition-all hover:shadow-lg hover:border-primary/20 border-s-4", catColors.border)}>
            <CardContent className="p-5">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={cn("text-xs border", catColors.badgeBg, catColors.badgeText)}>
                    <Wrench className="mr-1 h-3 w-3" />
                    {t(`annonces:categories_all.${catKey}`, { defaultValue: catKey })}
                  </Badge>
                  {annonce.urgency === "asap" && (
                    <Badge className="bg-destructive text-destructive-foreground text-xs">
                      <Zap className="mr-1 h-3 w-3" />{t("annonces:badge.urgent")}
                    </Badge>
                  )}
                </div>
                <h3 className="font-display text-lg font-semibold leading-tight line-clamp-1">{annonce.title}</h3>
                {annonce.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{annonce.description}</p>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {annonce.commune}, {annonce.wilaya}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {getUrgencyLabel(annonce.urgency, locale)}
                </span>
                {formatBudget(annonce.budget_min, annonce.budget_max) && (
                  <span className="flex items-center gap-1 font-medium text-foreground">
                    {formatBudget(annonce.budget_min, annonce.budget_max)}
                  </span>
                )}
              </div>

              <div className="mt-4 flex items-center justify-between border-t pt-3">
                <Badge variant="outline" className="text-[10px] gap-1 text-muted-foreground">
                  <Lock className="h-3 w-3" />
                  {t("preview.locked")}
                </Badge>
                <Link to={lp(`/annonce/${annonce.id}`)}>
                  <Button variant="ghost" size="sm" className="text-xs gap-1">
                    {t("preview.details")} <DirectionalArrow className="h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      <div className="mt-8 text-center">
        <Link to={lp("/annonces")}>
          <Button variant="outline" size="lg" className="gap-2">
            {t("preview.see_all")} <DirectionalArrow className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </section>
  );
};

export default AnnoncePreviewSection;
